import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from "react-router";
import { GRID_ROWS_PER_PAGE_DEFAULT_OPTION } from '../../../constants';
import { ROUTES } from "../../../constants/routes";
import { fetchApiPlansList as fetchApiPlansListAction } from "../../../actions/apiPlans";
import {
  getApiPlansList, getApiPlansListCount,
  getApiPlansLoading, getApiPlansValidationError,
} from '../../../reducers/apiPlans';
import ListContaienr from '../../list';
import { getI18n } from '../../../utils/intl';
import { getFeatureFlagApiPlans, getIsPortalAdmin, getIsApiOwner } from '../../../reducers/portalConfig';
import { makeStyles, Tooltip } from '@material-ui/core';
import cautionIcon from "../../../icons/caution.svg";
import styles from "../EnableApiPlans/styles";

const labels = {
  header: "label.api.plans",
  addButtonTitle: "label.apiPlans.list.add.button.title",
  planNameHeader: "label.apiPlans.list.name.column.title",
  description: "label.description",
  quota: "label.rate.quota.limit.quota",
  rateLimit: "label.rate.quota.rate.limit",
  noResults: "label.apiPlans.no.results",
  unlimited: "label.apiPlans.unlimited",
  second: "label.apiPlans.ratelimit.unit.second",
};

const getApiPlansListColumns = (intl) => [
  {
    id: 'name',
    label: intl.getI18nMessage(labels.planNameHeader),
    minWidth: 200,
    link: ROUTES.apiPlansEdit.path,
    isCellOverflow: true,
  },
  {
    id: 'description',
    label: intl.getI18nMessage(labels.description),
    minWidth: 300,
    isCellOverflow: true,
  },
  {
    id: 'quota',
    label: intl.getI18nMessage(labels.quota),
    minWidth: 100,
    value: (apiPlan) => {
      if (typeof apiPlan.quota !== 'number') {
        return intl.getI18nMessage(labels.unlimited);
      }
      if (!apiPlan?.quotaInterval) {
        return `${apiPlan.quota} / ${intl.getI18nMessage(labels.unlimited)}`
      }
      return `${apiPlan.quota} / ${intl.getI18nMessage(
        `label.apiPlans.quota.unit.${apiPlan?.quotaInterval?.toLowerCase()}`,
      )}`
    },
  },
  {
    id: 'rateLimit',
    label: intl.getI18nMessage(labels.rateLimit),
    minWidth: 100,
    value: (apiPlan) => {
      if (typeof apiPlan.rateLimit !== 'number') {
        return intl.getI18nMessage(labels.unlimited);
      }
      return `${apiPlan.rateLimit} / ${intl.getI18nMessage(labels.second)}`
    },
  },
];

const ApiPlansList = () => {
  const intl = getI18n(useIntl());
  const history = useHistory();
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const globalDispatch = useDispatch();
  const fetchApiPlansList = (payload) => globalDispatch(fetchApiPlansListAction(payload));

  const apiPlans = useSelector(getApiPlansList) ?? [];
  const totalApiPlans = useSelector(getApiPlansListCount);
  const isLoading = useSelector(getApiPlansLoading);
  const error = useSelector(getApiPlansValidationError);
  const isApiPlansEnabled = useSelector(getFeatureFlagApiPlans);
  const isPortalAdmin = useSelector(getIsPortalAdmin);
  const isApiOwner = useSelector(getIsApiOwner);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);

  useEffect(() => {
    if(!isPortalAdmin && !isApiOwner) {
      history.push('/404');
    }
    fetchApiPlansList({
      page: page,
      size: rowsPerPage,
    });
  }, []);

  useEffect(() => {
    if (!isApiPlansEnabled) {
      history.push(ROUTES.apiPlansEnable.path);
    }
  }, [isApiPlansEnabled]);

  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    fetchApiPlansList({
      page: newPage,
      size: rowsPerPage,
    });
  };

  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchApiPlansList({
      page: 0,
      size: newRowsPerPage,
    });
  };

  const onAddApiPlan = () => {
    history.push(ROUTES.apiPlansAdd.path);
  };

  const noResultsMessage = intl.getI18nMessage(labels.noResults);
  const totalPages = Math.ceil(Number(totalApiPlans / rowsPerPage));
  const apiPlansFetchErrors = error ? [{ error }] : [];

  return (
    <ListContaienr
      listPageId='api-plans-page'
      pageHeaderTitle={intl.getI18nMessage(labels.header)}
      addButtonLabel={intl.getI18nMessage(labels.addButtonTitle)}
      rows={apiPlans}
      columns={getApiPlansListColumns(intl)}
      onAdd={onAddApiPlan}
      noResultsMessage={noResultsMessage}
      page={page}
      totalElements={Number(totalApiPlans)}
      totalPages={totalPages}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onChangePage={onChangePage}
      onChangePreviousPage={onChangePreviousPage}
      onChangeNextPage={onChangeNextPage}
      isLoading={isLoading}
      errors={apiPlansFetchErrors}
      headerEndAdornment={
        <>
          <Tooltip
            title={intl.getI18nMessage("label.api.plans.enable.tool.tip.message")}
            arrow
            placement='right'
          >
            <img src={cautionIcon}
              className={classes.listToolTip}
            />
          </Tooltip>
        </>
      }
    />
  )
}

export default ApiPlansList;